import { Link } from 'react-router';
import { Mail, Phone, MapPin, Facebook, Linkedin } from 'lucide-react';
import { siteConfig } from '@afrilink/config';

/**
 * Footer — Switchboard-Modernist treatment, mirroring the Header vocabulary.
 * Mono "01/" eyebrow per column, Bricolage display logotype, primary hairline
 * along the top edge, hairline rule above the copyright row.
 */
export function Footer() {
  const currentYear = new Date().getFullYear();

  const columns = [
    {
      eyebrow: 'Sections',
      items: [
        { label: 'Web Hosting', href: '/hosting', external: false },
        { label: 'Domain Registration', href: '/domains', external: false },
        { label: 'Care & Email Setup', href: '/services/care', external: false },
        // Fibre Internet disabled 2026-05-08 — service not yet active.
        // Re-enable here + in marketing/src/config/navigation.ts (mainNav
        // and footerNav.services) + in marketing/src/routes.tsx when the
        // service launches.
        // { label: 'Fibre Internet', href: '/fibre', external: false },
        { label: 'About', href: '/about', external: false },
        { label: 'Support', href: '/support', external: false },
      ],
    },
    {
      eyebrow: 'Legal',
      items: [
        { label: 'Terms of Service', href: '/terms', external: false },
        { label: 'Acceptable Use Policy', href: '/aup', external: false },
        { label: 'Service Level Agreement', href: '/sla', external: false },
        { label: 'Privacy Policy', href: '/privacy', external: false },
        { label: 'Cookie Policy', href: '/cookies', external: false },
        { label: 'Refund Policy', href: '/refund', external: false },
      ],
    },
  ];

  return (
    <footer
      className="relative isolate border-t border-white/10 bg-neutral-950"
      role="contentinfo"
    >
      {/* Top primary hairline — visual continuation of Header's bottom edge */}
      <div
        aria-hidden="true"
        className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent"
      />

      <div className="container mx-auto px-4 py-5">
        <div className="grid gap-6 md:grid-cols-12">
          {/* Brand column */}
          <div className="md:col-span-3">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/35">
              <span className="text-primary/80">00/</span>Brand
            </p>
            <Link to="/" className="mt-2 inline-flex items-center">
              <img
                src="/images/logo.png"
                alt="SightHost"
                width={120}
                height={36}
                className="h-8 w-auto brightness-0 invert"
                loading="lazy"
                decoding="async"
              />
              {/* Brand-text block dropped 2026-07-01 per owner — see
                  Header.tsx for full rationale. Logo wordmark stands alone. */}
            </Link>
            <p className="mt-3 max-w-xs text-sm leading-relaxed text-white/55">
              {/* Fibre disabled 2026-05-08 — service is not yet active.
                  Original: "Proudly South African hosting, domains, and fibre
                  infrastructure — built local, supported local." Restore
                  when fibre service launches. */}
              Proudly South African hosting and domains — built local, supported local.
            </p>

            <div className="mt-3 flex items-start gap-2.5 text-sm text-white/55">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-primary/70" aria-hidden="true" />
              <span>
                {siteConfig.address.street}
                <br />
                {siteConfig.address.locality}, {siteConfig.address.city}{' '}
                {siteConfig.address.postalCode}
              </span>
            </div>
          </div>

          {/* Section + Legal columns */}
          {columns.map((column, i) => (
            <div key={column.eyebrow} className="md:col-span-3">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/35">
                <span className="text-primary/80">{String(i + 1).padStart(2, '0')}/</span>
                {column.eyebrow}
              </p>
              <ul className="mt-3 space-y-1.5">
                {column.items.map((item) => (
                  <li key={item.label}>
                    <Link
                      to={item.href}
                      className="text-sm text-white/65 transition-colors hover:text-primary"
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* Contact + Connect column */}
          <div className="md:col-span-3">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/35">
              <span className="text-primary/80">03/</span>Contact
            </p>
            <ul className="mt-3 space-y-2 text-sm">
              <li>
                <a
                  href={`mailto:${siteConfig.email}`}
                  className="flex items-center gap-2 text-white/65 transition-colors hover:text-primary"
                >
                  <Mail className="h-3.5 w-3.5 shrink-0 text-primary/70" aria-hidden="true" />
                  <span className="truncate">{siteConfig.email}</span>
                </a>
              </li>
              <li>
                <a
                  href={`tel:${siteConfig.phone}`}
                  className="flex items-center gap-2 text-white/65 transition-colors hover:text-primary"
                >
                  <Phone className="h-3.5 w-3.5 text-primary/70" aria-hidden="true" />
                  {siteConfig.phoneDisplay}
                </a>
              </li>
            </ul>

            <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.3em] text-white/35">
              <span className="text-primary/80">04/</span>Connect
            </p>
            <div className="mt-3 flex gap-2">
              {[
                { href: siteConfig.social.facebook, icon: Facebook, label: 'Facebook' },
                { href: siteConfig.social.linkedin, icon: Linkedin, label: 'LinkedIn' },
              ].map(({ href, icon: Icon, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Follow us on ${label}`}
                  className="brackets-hover flex h-9 w-9 items-center justify-center rounded-sm border border-white/10 bg-white/[0.03] text-white/60 transition-all hover:border-primary/40 hover:bg-white/[0.06] hover:text-primary"
                >
                  <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom row — hairline + copyright + registration */}
        <div className="mt-6 border-t border-white/10 pt-3">
          <div className="flex flex-col items-start justify-between gap-1 text-xs text-white/40 md:flex-row md:items-center">
            <p className="font-mono uppercase tracking-[0.15em]">
              &copy; {currentYear} {siteConfig.legalName}
            </p>
            <p className="font-mono uppercase tracking-[0.15em]">
              REG / {siteConfig.registration}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
